import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { TooltipHost } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";

import { InstancesListing } from "../index";

import { TEXT } from "../../../../../shared/constants/texts";
import { PAGE, PAGE_SIZE } from "../../../../../shared/constants/paginationConstants";
import { ICON } from "../../../../../shared/constants/icons";
import {
  UPLOAD_REQUEST_FROM,
  UPLOAD_TYPE,
  UPLOAD_FLOW,
  UPLOAD_DRAG_AND_DROP_SIZE_LIMIT,
} from "../../../../../shared/constants/uploadParams";
import { ROUTE } from "../../../../../shared/constants/routes";
import { logError } from "../../../../../shared/services/errorLogging";
import { bactInstanceApiCall, instanceApiCall } from "../../../redux/apiCalls";
import { callToUploadApi } from "../../../utils";
import {
  hideNotification,
  showNotificationWarning,
  startFetchingAssociationInstances,
  startUploadingDndAssociation,
  startUploadingDndAssociationInstance,
  stopUploadingDndAssociation,
  stopUploadingDndAssociationInstance,
} from "../../../redux/slice";
import {
  setSelectedAssociationData,
  setSelectedAssociationInstanceData,
  updateAssociationInstanceData,
} from "../../../utilFunctions";

const AssociationListing: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const {
    singleEmailData,
    areAttachmentsIncluded,
    isFetchingAssociationData,
    isFetchingFolderData,
    associationData,
    selectedAssociation,
    selectedAgreement,
    isUploadingDndAssociation,
    isFetchingAssociationInstances,
    associationInstancesData,
    isUploadingDndAssociationInstance,
    selectedAssociationInstance,
  } = state;
  const [dndAssociationFiles, setDndAssociationFiles] = React.useState([]);
  const [showInstancesId, setShowInstancesId] = React.useState(null);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (showInstancesId) {
      getAssociationInstances();
    } else {
      updateAssociationInstanceData([]);
    }
  }, [showInstancesId]);

  React.useEffect(() => {
    if (dndAssociationFiles.length > 0 && selectedAssociation) {
      const ifAttachmentsPresent =
        singleEmailData && singleEmailData.attachments && singleEmailData.attachments.length > 0;
      if (dndAssociationFiles.length === 1 && areAttachmentsIncluded && ifAttachmentsPresent) {
        history.push({ pathname: ROUTE.SINGLE_EMAIL_UPLOAD, state: { from: UPLOAD_REQUEST_FROM.ASSOCIATION } });
      } else {
        callToUploadApiHandler();
      }
    }
  }, [dndAssociationFiles, selectedAssociation]);

  const callToUploadApiHandler = async () => {
    let toBeUploaded = true;
    let base64Files = [];
    let uploadSize = 0;
    for (let file of dndAssociationFiles) {
      uploadSize = uploadSize + file.size;
      console.log("drag and drop upload size:", uploadSize);
      if (uploadSize >= UPLOAD_DRAG_AND_DROP_SIZE_LIMIT) {
        toBeUploaded = false;
        setDndAssociationFiles([]);
        dispatch(stopUploadingDndAssociation());
        dispatch(
          showNotificationWarning(TEXT.UPLOAD_SIZE_WARNING + UPLOAD_DRAG_AND_DROP_SIZE_LIMIT / 1000000 + TEXT.MB)
        );
        setTimeout(() => {
          dispatch(hideNotification());
        }, 5000);
        return;
      }
      const fileData = new Blob([file]);
      const b64 = await blobToBase64(fileData);
      base64Files.push({ name: file.name, b64: b64 });
    }
    if (toBeUploaded) {
      callToUploadApi({
        ...apiCallParams,
        state: state,
        dnd: true,
        type: UPLOAD_TYPE.NEW_DOCUMENT,
        from: UPLOAD_REQUEST_FROM.ASSOCIATION,
        flow: UPLOAD_FLOW.DRAG_AND_DROP,
        uploadData: {},
        files: base64Files,
      });
    }
    setDndAssociationFiles([]);
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = function () {
        resolve(reader.result);
      };
    });
  };

  const getAssociationInstances = async () => {
    dispatch(startFetchingAssociationInstances());
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateAssociationInstanceData(response);
      }
      if (error) {
        updateAssociationInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|association-listing|AssociationListing - getAssociationInstances",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_DND,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getAssociationInstances(),
      });
      const { response, error } = instanceResponse;
      if (response) {
        updateAssociationInstanceData(response);
      }
      if (error) {
        updateAssociationInstanceData([]);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|association-listing|AssociationListing - getAssociationInstances",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const onIconToggleClicked = (data) => {
    if (showInstancesId === data.EntityInstanceId) {
      setShowInstancesId(null);
      setSelectedAssociationData(null);
    } else {
      setShowInstancesId(data.EntityInstanceId);
      setSelectedAssociationData(data);
    }
  };

  const handleAssociationDnD = (e, data) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(startUploadingDndAssociation());
    const files = e.dataTransfer.files;
    let uploadSize = 0;
    for (let file of files) {
      uploadSize = uploadSize + file.size;
    }
    setSelectedAssociationData(data);
    setDndAssociationFiles(files);
    if (showInstancesId) {
      setShowInstancesId(null);
    }
  };

  const handleDnD = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleAssociationInstanceDnD = (e, data) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(startUploadingDndAssociationInstance());
    const files = e.dataTransfer.files;
    if (files.length === 1) {
      setSelectedAssociationInstanceData(data);
      callToUploadApi({
        ...apiCallParams,
        state: state,
        dnd: false,
        type: UPLOAD_TYPE.REPLACE_EXISTING,
        from: UPLOAD_REQUEST_FROM.ASSOCIATION_INSTANCE,
        flow: UPLOAD_FLOW.DRAG_AND_DROP,
        uploadData: { data: data, id: null },
      });
    } else {
      dispatch(stopUploadingDndAssociationInstance());
      dispatch(showNotificationWarning(TEXT.WARNING_MULTIPLE_DND_ON_INSTANCES));
    }
  };

  const onEnterPressAssociation = (e, association) => {
    if (e.key === TEXT.ENTER || e.keyCode === TEXT.ENTER_KEY_CODE) {
      onIconToggleClicked(association);
    }
  };

  return isFetchingAssociationData || isFetchingFolderData ? (
    <LoadingAssociations />
  ) : (
    <div className="association-listing-container-add-to-ici">
      {associationData && associationData.length > 0 ? (
        associationData.map((association) => {
          let classNameForAssociation = "single-association-container-add-to-ici";
          if (
            !isUploadingDndAssociation &&
            selectedAssociation &&
            selectedAssociation.EntityInstanceId === association.EntityInstanceId
          ) {
            classNameForAssociation = "single-association-container-selected-add-to-ici";
          }
          const iconName = showInstancesId === association.EntityInstanceId ? ICON.UP : ICON.DOWN;
          return (
            <React.Fragment key={association.EntityInstanceId}>
              <div
                className={classNameForAssociation}
                onDrop={(e) => handleAssociationDnD(e, association)}
                onDragOver={(e) => handleDnD(e)}
                onDragEnter={(e) => handleDnD(e)}
                onDragLeave={(e) => handleDnD(e)}
              >
                {!(
                  isUploadingDndAssociation &&
                  selectedAssociation &&
                  selectedAssociation.EntityInstanceId === association.EntityInstanceId
                ) ? (
                  <React.Fragment>
                    <div
                      className="icon-toggle"
                      onClick={() => onIconToggleClicked(association)}
                      tabIndex={1}
                      onKeyDown={(e) => onEnterPressAssociation(e, association)}
                      title={iconName}
                    >
                      <Icon iconName={iconName} />
                      <span className="sr-only">{iconName}</span>
                    </div>
                    <div className="association-label">
                      <TooltipHost id="association-tooltip-add-to-ici" content={association.name}>
                        <p>{association.name} </p>
                      </TooltipHost>
                    </div>
                  </React.Fragment>
                ) : (
                  <UploadToIci />
                )}
              </div>
              {showInstancesId && showInstancesId === association.EntityInstanceId ? (
                isFetchingAssociationInstances ? (
                  <LoadingAssociationIntances />
                ) : associationInstancesData && associationInstancesData.length > 0 ? (
                  <div
                    className="association-instance-listing-container-add-to-ici"
                    id="id-association-instances-listing-add-to-ici"
                  >
                    <InstancesListing
                      data={associationInstancesData}
                      idParam="id-association-instances-listing-add-to-ici"
                      loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
                      endMsg=""
                      hasMoreData={false}
                      loadMoreData={() => {}}
                      handleInstanceDnD={handleAssociationInstanceDnD}
                      isUploading={isUploadingDndAssociationInstance}
                      selectedData={selectedAssociationInstance}
                    />
                  </div>
                ) : (
                  <EmptyAssociationInstances />
                )
              ) : null}
            </React.Fragment>
          );
        })
      ) : (
        <EmptyAssociations />
      )}
    </div>
  );
};

const LoadingAssociations = () => {
  return (
    <div className="association-listing-loading-container-add-to-ici">
      <Spinner label={TEXT.LOADING_ASSOCIATIONS} labelPosition="right" />
    </div>
  );
};

const EmptyAssociations = () => {
  return (
    <div className="association-listing-empty-container-add-to-ici">
      <p>{TEXT.NO_ASSOCIATION_FOUND}</p>
    </div>
  );
};

const LoadingAssociationIntances = () => {
  return (
    <div className="association-instances-listing-loading-container-add-to-ici">
      <Spinner label={TEXT.LOADING_ASSOCIATION_INSTANCES} labelPosition="right" />
    </div>
  );
};

const EmptyAssociationInstances = () => {
  return (
    <div className="association-instances-listing-empty-container-add-to-ici">
      <p>{TEXT.NO_ASSOCIATION_INSTANCES_FOUND}</p>
    </div>
  );
};

const UploadToIci = () => {
  return (
    <div className="uploading-to-ici">
      <Spinner label={TEXT.UPLOADING_TO_ICI} labelPosition="right" />
    </div>
  );
};

export default AssociationListing;
